import React, { useCallback, useEffect, useState } from 'react';
import { Empty, FloatButton, Spin, Tabs, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import MonthSelector from '../layout/MonthSelector';
import ExpenseCard from './ExpenseCard';
import ExpenseFormModal from './ExpenseFormModal';
import api from '../common/api';
import { useCashFlow } from '../common/CashFlowContext';

const fmt = (n) => new Intl.NumberFormat('ko-KR').format(n);

function ExpenseList({ expenses, onPick }) {
  const total = expenses.reduce((s, e) => s + e.amount, 0);
  return (
    <div>
      <div style={{
        background: '#fff', padding: '12px 20px', borderRadius: 8, marginBottom: 12,
        display: 'flex', justifyContent: 'space-between',
      }}>
        <Typography.Text type="secondary">총 지출</Typography.Text>
        <Typography.Text strong>{fmt(total)}원</Typography.Text>
      </div>
      {expenses.length === 0 ? (
        <Empty description="지출 내역이 없어요" style={{ marginTop: 40 }} />
      ) : (
        expenses.map((e) => <ExpenseCard key={e.id} expense={e} onClick={() => onPick(e)} />)
      )}
    </div>
  );
}

export default function HomeScreen() {
  const { currentUserId, currentUser, otherUser, yearMonth } = useCashFlow();
  const [activeTab, setActiveTab] = useState('mine');
  const [expenses, setExpenses] = useState({ mine: [], other: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    if (currentUserId == null) return;
    setLoading(true);
    try {
      const otherId = otherUser?.id;
      const [mineRes, otherRes, allRes] = await Promise.all([
        api.get('/expenses', { params: { year: yearMonth.year, month: yearMonth.month, userId: currentUserId } }),
        otherId
          ? api.get('/expenses', { params: { year: yearMonth.year, month: yearMonth.month, userId: otherId } })
          : Promise.resolve({ data: [] }),
        api.get('/expenses', { params: { year: yearMonth.year, month: yearMonth.month } }),
      ]);
      setExpenses({ mine: mineRes.data, other: otherRes.data, all: allRes.data });
    } finally {
      setLoading(false);
    }
  }, [currentUserId, otherUser?.id, yearMonth]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePick = (expense) => {
    setEditing(expense);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', position: 'relative' }}>
      <MonthSelector />
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'mine', label: `내 지출 (${currentUser?.name || ''})` },
          { key: 'other', label: otherUser?.name || '상대' },
          { key: 'all', label: '합산' },
        ]}
      />
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <>
          {activeTab === 'mine' && <ExpenseList expenses={expenses.mine} onPick={handlePick} />}
          {activeTab === 'other' && <ExpenseList expenses={expenses.other} onPick={handlePick} />}
          {activeTab === 'all' && <ExpenseList expenses={expenses.all} onPick={handlePick} />}
        </>
      )}

      <FloatButton
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleAdd}
        style={{ backgroundColor: '#4A90D9' }}
      />

      <ExpenseFormModal
        open={modalOpen}
        expense={editing}
        onClose={() => setModalOpen(false)}
        onSaved={fetchAll}
      />
    </div>
  );
}
